import { Plus } from "lucide-react";
import React, { memo, useCallback } from "react";
import { useForm } from "react-hook-form";
import {
  PostChemicalTransportParams,
  usePostChemicalTransportData
} from "../../../../api/chemical";
import { Button } from "../../../../components/ui/common/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "../../../../components/ui/common/tooltip";
import { FormInputNumber } from "../../../../components/ui/Input/FormInputNumber/FormInputNumber";
import { FormSelect } from "../../../../components/ui/Input/FormSelector/FormSelect";
import { errorView, successMessage } from "../../../../helpers/ErrorHandler";
import {
  ChemicalTransportContextProps,
  ChemicalTransportRouteFormValues
} from "./chemicalTransport.types";
import { validateChemicalRoute } from "./chemicalTransport.utils";

const DEFAULT_VALUES: ChemicalTransportRouteFormValues = {
  transportationType: "",
  distance: ""
};

const AddChemicalTransportRoute: React.FC<ChemicalTransportContextProps> = ({
  lockStatus,
  TransportTypeList = [],
  chemicalId,
  type,
  supplierDataHandle,
  processId,
  onChemicalSave,
  refetchChemical,
  refetchTransportations
}) => {
  const form = useForm<ChemicalTransportRouteFormValues>({ defaultValues: DEFAULT_VALUES });
  const { mutate: createTransport } = usePostChemicalTransportData();

  const handleSave = useCallback(() => {
    const values = form.getValues();
    if (!validateChemicalRoute(values, chemicalId)) return;

    const payload: PostChemicalTransportParams = {
      supplierDataHandle: supplierDataHandle ?? false,
      type: type ?? "",
      transportData: {
        transportType: values.transportationType,
        distance: parseFloat(values.distance)
      },
      chemicalId: chemicalId ?? ""
    };

    createTransport(payload, {
      onSuccess: response => {
        successMessage("Transport route created successfully");
        form.reset(DEFAULT_VALUES);

        refetchTransportations();

        if (onChemicalSave && processId) {
          onChemicalSave(processId);
        }
      },
      onError: (error: any) => {
        errorView(error?.msg || "Failed to create transport route");
      }
    });
  }, [
    form,
    chemicalId,
    type,
    supplierDataHandle,
    createTransport,
    onChemicalSave,
    processId,
    refetchTransportations
  ]);

  return (
    <div className="tw-pt-1">
      <p className="tw-text-[9px] tw-text-muted-foreground tw-mb-1">Add transport route</p>
      <div className="tw-grid tw-grid-cols-[1fr_250px_36px] tw-gap-2 tw-items-end">
        <FormSelect
          control={form.control}
          name="transportationType"
          label=""
          placeholder="Transport type"
          options={TransportTypeList}
          disabled={lockStatus}
        />
        <FormInputNumber
          control={form.control}
          name="distance"
          label=""
          placeholder="Dist."
          rightContentEnable
          rightContent="km"
          disabled={lockStatus}
          removeDecimal={false}
        />
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="default"
                size="icon"
                className="!tw-h-10 tw-w-9"
                onClick={handleSave}
                disabled={lockStatus}
              >
                <Plus className="tw-h-4 tw-w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="tw-text-xs">Save route</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};

export default memo(AddChemicalTransportRoute);
